function getSharedFirstFailedValidationResult(results) {
  if (
    typeof module !== 'undefined' &&
    module.exports &&
    typeof require === 'function'
  ) {
    const helpers = require('./tour-form-helpers.js');
    if (helpers && typeof helpers.findFirstFailedValidationResult === 'function') {
      return helpers.findFirstFailedValidationResult(results);
    }
  }
  if (
    typeof window !== 'undefined' &&
    window.MFTourFormHelpers &&
    typeof window.MFTourFormHelpers.findFirstFailedValidationResult === 'function'
  ) {
    return window.MFTourFormHelpers.findFirstFailedValidationResult(results);
  }
  const normalizedResults = Array.isArray(results) ? results : [];
  return normalizedResults.find((result) => result && !result.ok) || null;
}

function buildTourValidationStateFallback({
  minorConsentRequired,
  agePricingState,
  expectedVariantId,
  results,
} = {}) {
  const firstFailure = getSharedFirstFailedValidationResult(results);
  const state = {
    birthdateOk: true,
    agePricingOk: true,
    attendeesOk: true,
    minorConsentRequired: !!minorConsentRequired,
    inlineErrorShown: false,
    message: '',
    agePricingState: String(agePricingState || ''),
    expectedVariantId: String(expectedVariantId || ''),
  };
  if (!firstFailure) return state;

  const flagKeyBySection = {
    birthdate: 'birthdateOk',
    agePricing: 'agePricingOk',
    attendees: 'attendeesOk',
  };
  const flagKey = flagKeyBySection[firstFailure.section];
  if (flagKey) state[flagKey] = false;
  state.inlineErrorShown = !!firstFailure.inlineErrorShown;
  state.message = String(firstFailure.message || '');
  return state;
}

if (typeof module !== 'undefined' && module.exports) {
  module.exports = { buildTourValidationStateFallback };
}

if (typeof window !== 'undefined' && typeof document !== 'undefined') {
  (() => {
  const registry = (window.__mfTourAttendees = window.__mfTourAttendees || { instances: {} });

  function escapeCss(value) {
    if (typeof CSS !== 'undefined' && typeof CSS.escape === 'function') {
      return CSS.escape(String(value || ''));
    }
    return String(value || '').replace(/"/g, '\\"');
  }

  function getConfigNodes(root) {
    const scope = root && root.querySelectorAll ? root : document;
    return Array.from(scope.querySelectorAll('[data-mf-tour-attendees-config]'));
  }

  function parseConfig(configEl) {
    if (!configEl) return null;
    try {
      const config = JSON.parse(configEl.textContent || '{}');
      return config && typeof config === 'object' ? config : null;
    } catch (error) {
      console.error('mf-tour-attendees config parse error', error);
      return null;
    }
  }

  function createInstanceKey(config) {
    return `${String(config.sectionId || '')}:${String(config.productId || '')}`;
  }

  function findSectionRoot(config) {
    const sectionRootId = String(config.sectionRootId || '');
    const sectionId = String(config.sectionId || '');
    return (
      (sectionRootId ? document.getElementById(sectionRootId) : null) ||
      (sectionId ? document.querySelector(`[data-section="${escapeCss(sectionId)}"]`) : null)
    );
  }

  function mountTourAttendees(configEl) {
    const config = parseConfig(configEl);
    if (!config) return;

    const instanceKey = createInstanceKey(config);
    const existingInstance = registry.instances[instanceKey];
    if (existingInstance && existingInstance.root && existingInstance.root.isConnected) return;
    delete registry.instances[instanceKey];

    try {
      const PRODUCT_ID = String(config.productId || '');
      const FORM_ID = String(config.formId || '');
      const DEFAULT_VARIANT_ID = String(config.defaultVariantId || '');
      const IS_TOUR = !!config.isTour;
      const IS_AGE_PRICING_TOUR = !!config.isAgePricingTour;
      const RAW_AGE_REFERENCE_DATE_ISO = String(config.rawAgeReferenceDateIso || '');
      const VARIANT_LIMITS = config.variantLimits || {};
      const AGE_PRICING_VARIANTS = Array.isArray(config.agePricingVariants) ? config.agePricingVariants : [];
      const SECTION_ID = String(config.sectionId || '');
      const INVENTORY_ELEMENT_ID = String(config.inventoryElementId || '');
      const TEMPLATE_ID = String(config.templateId || '');

      registry.instances[instanceKey] = { status: 'booting', root: null };
      if (!IS_TOUR) {
        registry.instances[instanceKey] = { status: 'skipped', root: null };
        return;
      }

      const sectionRoot = findSectionRoot(config);
      if (!sectionRoot) {
        registry.instances[instanceKey] = { status: 'no-section', root: null };
        return;
      }

      const form = (() => {
        const sectionForm = sectionRoot.querySelector('product-form form[action*="/cart/add"]');
        if (sectionForm) return sectionForm;
        return document.querySelector('form[action*="/cart/add"]');
      })();

      if (!form) {
        registry.instances[instanceKey] = { status: 'no-form', root: sectionRoot };
        return;
      }

      const template = document.getElementById(TEMPLATE_ID);
      if (!template || !template.content || !template.content.firstElementChild) {
        registry.instances[instanceKey] = { status: 'no-template', root: sectionRoot };
        return;
      }

      const qty = document.querySelector(`.quantity__input[form="${FORM_ID}"], input[name="quantity"][form="${FORM_ID}"]`);
      const qtyWrap = qty && qty.closest('quantity-input');
      const priceContainer = qty && qty.closest('.price-per-item__container');
      const variantIdInput = form.querySelector('input[name="id"]');
      let platformMax = qty ? parseFiniteInt(qty.getAttribute('max')) : null;
      let appliedMax = qty ? parseFiniteInt(qty.getAttribute('max')) : null;
      let limitWarningEl = null;
      let limitWarningActive = false;
      let agePricingSyncing = false;
      let pendingAgePricingVariantId = null;
      let hiddenBd;
      let bdError;
      let agePricingStateInput;
      let agePricingExpectedVariantInput;
      let agePricingAgeInput;
      let agePricingBandInput;
      let agePricingReferenceInput;
      let agePricingStateModel = null;

      function defaultParseFiniteInt(value) {
        const normalized = String(value ?? '').trim();
        if (!/^[+-]?\d+$/.test(normalized)) return null;
        const n = Number.parseInt(normalized, 10);
        return Number.isSafeInteger(n) ? n : null;
      }

      function getMFTourFormHelpers() {
        if (typeof window === 'undefined' || !window.MFTourFormHelpers || typeof window.MFTourFormHelpers !== 'object') {
          return {};
        }
        return window.MFTourFormHelpers;
      }

      function parseFiniteInt(value) {
        const helpers = getMFTourFormHelpers();
        const fn = typeof helpers.parseFiniteInt === 'function' ? helpers.parseFiniteInt : defaultParseFiniteInt;
        return fn(value);
      }

      function calculateAgeOnReferenceDate(birthdateIso, referenceIso) {
        const helpers = getMFTourFormHelpers();
        const fn = typeof helpers.calculateAgeOnDate === 'function' ? helpers.calculateAgeOnDate : null;
        if (!fn) return null;
        return fn(birthdateIso, referenceIso);
      }

      function isMinorAge(age) {
        const helpers = getMFTourFormHelpers();
        const fn = typeof helpers.isMinorAge === 'function' ? helpers.isMinorAge : null;
        if (fn) return fn(age);
        return Number.isFinite(age) && age >= 0 && age < 18;
      }

      function hasMinorParticipant(applicantAge, attendeeAges) {
        const helpers = getMFTourFormHelpers();
        const fn = typeof helpers.hasMinorParticipant === 'function' ? helpers.hasMinorParticipant : null;
        if (fn) return fn({ applicantAge, attendeeAges });
        if (isMinorAge(applicantAge)) return true;
        return attendeeAges.some(isMinorAge);
      }

      const ageReferenceResolution = (() => {
        const helpers = getMFTourFormHelpers();
        const fn = typeof helpers.resolveAgeReferenceDate === 'function' ? helpers.resolveAgeReferenceDate : null;
        if (fn) return fn(RAW_AGE_REFERENCE_DATE_ISO);
        return {
          value: String(RAW_AGE_REFERENCE_DATE_ISO || '').trim(),
          source: RAW_AGE_REFERENCE_DATE_ISO ? 'configured' : 'missing',
        };
      })();
      const AGE_REFERENCE_DATE_ISO = String(ageReferenceResolution.value || '');
      const minorConsentReferenceResolution = (() => {
        const helpers = getMFTourFormHelpers();
        const fn =
          typeof helpers.resolveMinorConsentReferenceDate === 'function'
            ? helpers.resolveMinorConsentReferenceDate
            : null;
        if (fn) return fn(RAW_AGE_REFERENCE_DATE_ISO);
        return ageReferenceResolution;
      })();
      const MINOR_CONSENT_REFERENCE_DATE_ISO = String(minorConsentReferenceResolution.value || '');

      function getApplicantBirthdateIso() {
        return hiddenBd && hiddenBd.value ? hiddenBd.value : '';
      }

      function getCurrentVariantId() {
        return variantIdInput && typeof variantIdInput.value === 'string' ? variantIdInput.value.trim() : DEFAULT_VARIANT_ID;
      }

      function normalizedAgeVariants() {
        const helpers = getMFTourFormHelpers();
        if (typeof helpers.normalizeAgePricingVariants === 'function') {
          return helpers.normalizeAgePricingVariants(AGE_PRICING_VARIANTS);
        }
        return AGE_PRICING_VARIANTS.map((variant) => ({
          id: String(variant.id || ''),
          title: String(variant.title || ''),
          available: !!variant.available,
          options: Array.isArray(variant.options) ? variant.options.map((value) => String(value)) : [],
          ageMin: parseFiniteInt(variant.ageMin),
          ageMax: parseFiniteInt(variant.ageMax),
        })).filter((variant) => variant.id);
      }

      function getCurrentVariantLimit() {
        const variantId = getCurrentVariantId();
        const helpers = getMFTourFormHelpers();
        if (typeof helpers.getVariantLimit === 'function') {
          return helpers.getVariantLimit(VARIANT_LIMITS, variantId);
        }
        if (!variantId) return null;
        return VARIANT_LIMITS[variantId] || null;
      }

      function getInventoryMax(limit) {
        const helpers = getMFTourFormHelpers();
        if (typeof helpers.getInventoryMax === 'function') {
          return helpers.getInventoryMax(limit);
        }
        if (!limit) return null;
        const inventoryQuantity = parseFiniteInt(limit.inventoryQuantity);
        if (!Number.isFinite(inventoryQuantity)) return null;
        return Math.max(0, inventoryQuantity);
      }

      function getVariantMax() {
        const helpers = getMFTourFormHelpers();
        if (typeof helpers.getVariantMax === 'function') {
          return helpers.getVariantMax({
            variantId: getCurrentVariantId(),
            variantLimits: VARIANT_LIMITS,
          });
        }
        const limit = getCurrentVariantLimit();
        let max = parseFiniteInt(limit && limit.quantityMax);
        const inventoryMax = getInventoryMax(limit);
        if (Number.isFinite(inventoryMax)) {
          max = Number.isFinite(max) ? Math.min(max, inventoryMax) : inventoryMax;
        }
        return Number.isFinite(max) ? max : null;
      }

      function getEffectiveMax() {
        const helpers = getMFTourFormHelpers();
        if (typeof helpers.getEffectiveMax === 'function') {
          return helpers.getEffectiveMax({
            platformMax,
            variantId: getCurrentVariantId(),
            variantLimits: VARIANT_LIMITS,
          });
        }
        const variantMax = getVariantMax();
        if (Number.isFinite(platformMax) && Number.isFinite(variantMax)) {
          return Math.min(platformMax, variantMax);
        }
        return Number.isFinite(platformMax) ? platformMax : variantMax;
      }

      function applyQtyMaxConstraint() {
        const effectiveMax = getEffectiveMax();
        if (!qty) return effectiveMax;
        appliedMax = effectiveMax;
        const currentMax = parseFiniteInt(qty.getAttribute('max'));
        if (Number.isFinite(effectiveMax)) {
          if (currentMax !== effectiveMax) qty.setAttribute('max', String(effectiveMax));
        } else if (qty.hasAttribute('max')) {
          qty.removeAttribute('max');
        }
        return effectiveMax;
      }

      function ensureLimitWarning() {
        const effectiveMax = getEffectiveMax();
        if (!Number.isFinite(effectiveMax)) return null;
        if (!limitWarningEl) {
          limitWarningEl = document.createElement('p');
          limitWarningEl.className = 'tour-attendees__limit-warning';
          limitWarningEl.setAttribute('role', 'alert');
          limitWarningEl.hidden = true;
        }
        limitWarningEl.textContent = `最大人数（代表者含む）: ${effectiveMax}名までご予約いただけます。`;
        const anchor = priceContainer || qtyWrap || qty;
        if (anchor && !limitWarningEl.isConnected) anchor.insertAdjacentElement('afterend', limitWarningEl);
        return limitWarningEl;
      }

      function showLimitWarning() {
        const el = ensureLimitWarning();
        if (el) {
          el.hidden = false;
          limitWarningActive = true;
        }
      }

      function hideLimitWarning() {
        if (limitWarningEl && limitWarningActive) {
          limitWarningEl.hidden = true;
          limitWarningActive = false;
        }
      }

      const rootId = `tour-attendees-root-${PRODUCT_ID}`;
      if (form.querySelector(`#${escapeCss(rootId)}`)) {
        registry.instances[instanceKey] = { status: 'exists', root: sectionRoot };
        return;
      }

      const fieldset = document.createElement('fieldset');
      fieldset.className = 'tour-attendees';
      fieldset.id = rootId;
      const legend = document.createElement('legend');
      legend.className = 'tour-attendees__legend';
      legend.textContent = '同行者様の情報の入力をお願い致します';
      const status = document.createElement('div');
      status.className = 'tour-attendees__status';
      status.setAttribute('aria-live', 'polite');
      const grid = document.createElement('div');
      grid.className = 'tour-attendees__grid';
      const note = document.createElement('div');
      note.className = 'tour-attendees__note';

      function updateLimitNote() {
        const effectiveMax = getEffectiveMax();
        if (Number.isFinite(effectiveMax)) {
          const attendeeCap = Math.max(0, effectiveMax - 1);
          note.textContent = `最大人数（代表者含む）: ${effectiveMax}名 / 同行者の上限: ${attendeeCap}名`;
        } else {
          note.textContent = '';
        }
      }

      fieldset.append(legend, status, grid, note);
      updateLimitNote();

      const birthdateRootId = `tour-applicant-birthdate-${PRODUCT_ID}`;
      let birthdateSection = form.querySelector(`#${escapeCss(birthdateRootId)}`);

      function getBirthdateContractElements(root) {
        return {
          hiddenBd: root.querySelector('[data-birthdate-hidden]'),
          bdError: root.querySelector('[data-birthdate-error]'),
          agePricingStateInput: root.querySelector('[data-age-pricing-state]'),
          agePricingExpectedVariantInput: root.querySelector('[data-age-pricing-expected-variant]'),
          agePricingAgeInput: root.querySelector('[data-age-pricing-age]'),
          agePricingBandInput: root.querySelector('[data-age-pricing-band]'),
          agePricingReferenceInput: root.querySelector('[data-age-pricing-reference-date]'),
        };
      }

      function createAgePricingContractSnapshot(stateValue, matchedVariant, ageValue) {
        const helpers = getMFTourFormHelpers();
        if (typeof helpers.createAgePricingContractState === 'function') {
          return helpers.createAgePricingContractState({
            state: stateValue,
            matchedVariant,
            ageValue,
            referenceDate: AGE_REFERENCE_DATE_ISO,
          });
        }
        return {
          state: String(stateValue || ''),
          expectedVariantId: matchedVariant ? String(matchedVariant.id) : '',
          ageValue: Number.isFinite(ageValue) ? String(ageValue) : '',
          bandValue: matchedVariant ? String(matchedVariant.title || '') : '',
          referenceDate: String(AGE_REFERENCE_DATE_ISO || ''),
        };
      }

      function getDefaultAgePricingContractSnapshot() {
        return {
          state: IS_AGE_PRICING_TOUR ? 'pending' : 'disabled',
          expectedVariantId: '',
          ageValue: '',
          bandValue: '',
          referenceDate: '',
        };
      }

      const AGE_PRICING_SNAPSHOT_KEYS = ['state', 'expectedVariantId', 'ageValue', 'bandValue', 'referenceDate'];
      const AGE_PRICING_INPUT_KEY_MAP = {
        state: () => agePricingStateInput,
        expectedVariantId: () => agePricingExpectedVariantInput,
        ageValue: () => agePricingAgeInput,
        bandValue: () => agePricingBandInput,
        referenceDate: () => agePricingReferenceInput,
      };

      function mergeAgePricingContractSnapshot(overrides = {}) {
        return Object.assign({}, getDefaultAgePricingContractSnapshot(), overrides);
      }

      function getAgePricingSnapshotFallbackValue(value, fallback) {
        return String(value || fallback);
      }

      function getDefaultAgePricingSnapshotValue(key) {
        return getDefaultAgePricingContractSnapshot()[key] || '';
      }

      function normalizeAgePricingSnapshotValues(source = {}) {
        return AGE_PRICING_SNAPSHOT_KEYS.reduce((snapshot, key) => {
          snapshot[key] = getAgePricingSnapshotFallbackValue(source[key], getDefaultAgePricingSnapshotValue(key));
          return snapshot;
        }, {});
      }

      function getAgePricingContractInputValue(key) {
        const inputResolver = AGE_PRICING_INPUT_KEY_MAP[key];
        const input = inputResolver ? inputResolver() : null;
        return input ? input.value : getDefaultAgePricingSnapshotValue(key);
      }

      function getRawAgePricingSnapshotFromInputs() {
        return AGE_PRICING_SNAPSHOT_KEYS.reduce((snapshot, key) => {
          snapshot[key] = getAgePricingContractInputValue(key);
          return snapshot;
        }, {});
      }

      function getRawAgePricingSnapshotFromModel() {
        if (!agePricingStateModel) return getDefaultAgePricingContractSnapshot();
        return AGE_PRICING_SNAPSHOT_KEYS.reduce((snapshot, key) => {
          snapshot[key] = agePricingStateModel[key];
          return snapshot;
        }, {});
      }

      function createAgePricingContractSnapshotFromSource(source = {}) {
        return mergeAgePricingContractSnapshot(normalizeAgePricingSnapshotValues(source));
      }

      function createAgePricingContractSnapshotFromInputs() {
        return createAgePricingContractSnapshotFromSource(getRawAgePricingSnapshotFromInputs());
      }

      function createAgePricingContractSnapshotFromModel() {
        return createAgePricingContractSnapshotFromSource(getRawAgePricingSnapshotFromModel());
      }

      function syncAgePricingStateModelFromInputs() {
        agePricingStateModel = createAgePricingContractSnapshotFromInputs();
      }

      function applyAgePricingContractSnapshot(snapshot) {
        agePricingStateModel = snapshot;
        AGE_PRICING_SNAPSHOT_KEYS.forEach((key) => {
          const inputResolver = AGE_PRICING_INPUT_KEY_MAP[key];
          const input = inputResolver ? inputResolver() : null;
          if (input) input.value = snapshot[key];
        });
      }

      function getAgePricingContractState() {
        if (agePricingStateModel) return createAgePricingContractSnapshotFromModel();
        return createAgePricingContractSnapshotFromInputs();
      }

      if (!birthdateSection) {
        birthdateSection = document.createElement('div');
        birthdateSection.className = 'tour-applicant-birthdate';
        birthdateSection.id = birthdateRootId;

        const bdTitle = document.createElement('p');
        bdTitle.className = 'tour-applicant-birthdate__title';
        bdTitle.textContent = '申込者（代表者）の生年月日（必須）';
        const birthdateSectionChildren = [bdTitle];
        if (IS_AGE_PRICING_TOUR) {
          const bdChildNote = document.createElement('p');
          bdChildNote.className = 'tour-applicant-birthdate__child-note';
          bdChildNote.textContent = 'お子様が参加される場合はお子様の生年月日をご入力下さい';
          birthdateSectionChildren.push(bdChildNote);
        }

        const bdSelects = document.createElement('div');
        bdSelects.className = 'tour-birthdate-selects';

        const yearSel = document.createElement('select');
        yearSel.setAttribute('data-birthdate-year', '');
        yearSel.setAttribute('aria-label', '生年月日：年');
        const yearDefault = document.createElement('option');
        yearDefault.value = '';
        yearDefault.textContent = '年';
        yearSel.appendChild(yearDefault);
        const currentYear = new Date().getFullYear();
        for (let y = currentYear; y >= 1920; y -= 1) {
          const option = document.createElement('option');
          option.value = String(y);
          option.textContent = `${y}年`;
          yearSel.appendChild(option);
        }

        const sep1 = document.createElement('span');
        sep1.className = 'tour-birthdate-sep';
        sep1.textContent = '/';

        const monthSel = document.createElement('select');
        monthSel.setAttribute('data-birthdate-month', '');
        monthSel.setAttribute('aria-label', '生年月日：月');
        const monthDefault = document.createElement('option');
        monthDefault.value = '';
        monthDefault.textContent = '月';
        monthSel.appendChild(monthDefault);
        for (let m = 1; m <= 12; m += 1) {
          const option = document.createElement('option');
          option.value = String(m);
          option.textContent = `${m}月`;
          monthSel.appendChild(option);
        }

        const sep2 = document.createElement('span');
        sep2.className = 'tour-birthdate-sep';
        sep2.textContent = '/';

        const daySel = document.createElement('select');
        daySel.setAttribute('data-birthdate-day', '');
        daySel.setAttribute('aria-label', '生年月日：日');
        const dayDefault = document.createElement('option');
        dayDefault.value = '';
        dayDefault.textContent = '日';
        daySel.appendChild(dayDefault);
        for (let d = 1; d <= 31; d += 1) {
          const option = document.createElement('option');
          option.value = String(d);
          option.textContent = `${d}日`;
          daySel.appendChild(option);
        }

        bdSelects.append(yearSel, sep1, monthSel, sep2, daySel);

        hiddenBd = document.createElement('input');
        hiddenBd.type = 'hidden';
        hiddenBd.name = 'properties[申込者_生年月日]';
        hiddenBd.value = '';
        hiddenBd.setAttribute('data-birthdate-hidden', '');

        agePricingStateInput = document.createElement('input');
        agePricingStateInput.type = 'hidden';
        agePricingStateInput.value = IS_AGE_PRICING_TOUR ? 'pending' : 'disabled';
        agePricingStateInput.setAttribute('data-age-pricing-state', '');

        agePricingExpectedVariantInput = document.createElement('input');
        agePricingExpectedVariantInput.type = 'hidden';
        agePricingExpectedVariantInput.value = '';
        agePricingExpectedVariantInput.setAttribute('data-age-pricing-expected-variant', '');

        agePricingAgeInput = document.createElement('input');
        agePricingAgeInput.type = 'hidden';
        agePricingAgeInput.value = '';
        agePricingAgeInput.disabled = !IS_AGE_PRICING_TOUR;
        agePricingAgeInput.name = 'properties[判定年齢]';
        agePricingAgeInput.setAttribute('data-age-pricing-age', '');

        agePricingBandInput = document.createElement('input');
        agePricingBandInput.type = 'hidden';
        agePricingBandInput.value = '';
        agePricingBandInput.disabled = !IS_AGE_PRICING_TOUR;
        agePricingBandInput.name = 'properties[価格区分]';
        agePricingBandInput.setAttribute('data-age-pricing-band', '');

        agePricingReferenceInput = document.createElement('input');
        agePricingReferenceInput.type = 'hidden';
        agePricingReferenceInput.value = '';
        agePricingReferenceInput.disabled = !IS_AGE_PRICING_TOUR;
        agePricingReferenceInput.name = 'properties[年齢判定基準日]';
        agePricingReferenceInput.setAttribute('data-age-pricing-reference-date', '');

        bdError = document.createElement('p');
        bdError.className = 'tour-applicant-birthdate__error';
        bdError.setAttribute('data-birthdate-error', '');
        bdError.hidden = true;

        birthdateSectionChildren.push(
          bdSelects,
          hiddenBd,
          agePricingStateInput,
          agePricingExpectedVariantInput,
          agePricingAgeInput,
          agePricingBandInput,
          agePricingReferenceInput,
          bdError
        );
        birthdateSection.append(...birthdateSectionChildren);

        function updateBirthdateHidden() {
          const y = yearSel.value;
          const m = monthSel.value;
          const d = daySel.value;
          hiddenBd.value = y && m && d ? `${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}` : '';
          bdError.hidden = true;
          bdError.textContent = '';
          yearSel.removeAttribute('aria-invalid');
          monthSel.removeAttribute('aria-invalid');
          daySel.removeAttribute('aria-invalid');
          refreshParticipantState();
        }

        function updateDays() {
          const y = parseFiniteInt(yearSel.value);
          const m = parseFiniteInt(monthSel.value);
          const prevDay = daySel.value;
          let maxDay = 31;
          if (Number.isFinite(m) && m >= 1 && m <= 12) {
            const yearForCalc = Number.isFinite(y) ? y : 2000;
            maxDay = new Date(yearForCalc, m, 0).getDate();
          }
          const currentMax = daySel.querySelectorAll('option').length - 1;
          if (currentMax < maxDay) {
            for (let d = currentMax + 1; d <= maxDay; d += 1) {
              const option = document.createElement('option');
              option.value = String(d);
              option.textContent = `${d}日`;
              daySel.appendChild(option);
            }
          } else if (currentMax > maxDay) {
            for (let d = currentMax; d > maxDay; d -= 1) {
              daySel.removeChild(daySel.lastChild);
            }
          }
          if (parseFiniteInt(prevDay) > maxDay) daySel.value = String(maxDay);
          updateBirthdateHidden();
        }

        yearSel.addEventListener('change', updateDays);
        monthSel.addEventListener('change', updateDays);
        daySel.addEventListener('change', updateBirthdateHidden);
      } else {
        ({
          hiddenBd,
          bdError,
          agePricingStateInput,
          agePricingExpectedVariantInput,
          agePricingAgeInput,
          agePricingBandInput,
          agePricingReferenceInput,
        } = getBirthdateContractElements(birthdateSection));
      }

      syncAgePricingStateModelFromInputs();

      const buttons = form.querySelector('.product-form__buttons');
      if (buttons && buttons.parentNode === form) {
        form.insertBefore(birthdateSection, buttons);
        if (!IS_AGE_PRICING_TOUR) form.insertBefore(fieldset, buttons);
      } else {
        form.appendChild(birthdateSection);
        if (!IS_AGE_PRICING_TOUR) form.appendChild(fieldset);
      }

      let state = [];
      function collectAttendeeRowState(row) {
        const { nameInput, ageInput, genderInput, telInput } = getAttendeeRowFields(row);
        return {
          name: nameInput?.value || '',
          age: ageInput?.value || '',
          gender: genderInput?.value || '',
          tel: telInput?.value || '',
        };
      }

      function collectAttendeeStates() {
        return getAttendeeRows().map((row) => collectAttendeeRowState(row));
      }

      function setAttendeeStates(nextState) {
        state = nextState;
      }

      function snapshot() {
        setAttendeeStates(collectAttendeeStates());
      }

      function clampQty(n) {
        const effectiveMax = getEffectiveMax();
        const helpers = getMFTourFormHelpers();
        if (typeof helpers.clampTourQuantity === 'function') {
          return helpers.clampTourQuantity({
            quantity: n,
            effectiveMax,
          });
        }
        if (Number.isFinite(effectiveMax) && effectiveMax <= 0) return 0;
        if (!Number.isFinite(n)) return 1;
        if (n < 1) return 1;
        if (Number.isFinite(effectiveMax) && n > effectiveMax) return effectiveMax;
        return n;
      }

      function attendeeCountFromQty(value) {
        const effectiveMax = getEffectiveMax();
        const helpers = getMFTourFormHelpers();
        if (typeof helpers.getAttendeeCountFromQuantity === 'function') {
          return helpers.getAttendeeCountFromQuantity({
            quantity: value,
            effectiveMax,
            isAgePricingTour: IS_AGE_PRICING_TOUR,
          });
        }
        if (IS_AGE_PRICING_TOUR) return 0;
        const q = clampQty(value);
        return Math.max(0, q - 1);
      }

      function normalizePhoneForValidation(raw) {
        const helpers = getMFTourFormHelpers();
        if (typeof helpers.normalizePhoneForValidation === 'function') {
          return helpers.normalizePhoneForValidation(raw);
        }
        if (!raw) return '';
        const v = String(raw)
          .trim()
          .replace(/[０-９]/g, (ch) => String.fromCharCode(ch.charCodeAt(0) - 65248))
          .replace(/[‐‑‒–—―ー−]/g, '-')
          .replace(/[()\s-]/g, '');
        if (v.startsWith('+81')) return `0${v.slice(3)}`;
        return v;
      }

      function isValidPhoneNumber(raw) {
        const helpers = getMFTourFormHelpers();
        if (typeof helpers.isValidPhoneNumber === 'function') {
          return helpers.isValidPhoneNumber(raw);
        }
        const normalized = normalizePhoneForValidation(raw);
        if (!normalized) return true;
        return /^0\d{9,10}$/.test(normalized);
      }

      function getFieldErrorEl(inputEl) {
        if (!inputEl) return null;
        return inputEl.parentElement ? inputEl.parentElement.querySelector('[data-field-error]') : null;
      }

      function setFieldErrorState(inputEl, message = '') {
        if (!inputEl) return false;
        const err = getFieldErrorEl(inputEl);
        const hasMessage = !!message;
        if (hasMessage) {
          inputEl.setAttribute('aria-invalid', 'true');
        } else {
          inputEl.removeAttribute('aria-invalid');
        }
        if (!err) return false;
        err.textContent = message;
        err.hidden = !hasMessage;
        return hasMessage;
      }

      function clearFieldError(inputEl) {
        setFieldErrorState(inputEl);
      }

      function clearAllFieldErrors() {
        grid.querySelectorAll('.tour-attendee input, .tour-attendee select').forEach(clearFieldError);
      }

      function showFieldError(inputEl, message) {
        return setFieldErrorState(inputEl, message);
      }

      function buildRowTemplate(index) {
        const node = template.content.firstElementChild.cloneNode(true);
        const fields = node.querySelectorAll('label, input, select');
        const idx = index + 1;
        const idBase = `attendee-${PRODUCT_ID}-${idx}-`;
        fields[0].setAttribute('for', `${idBase}name`);
        fields[0].textContent = `同行者${idx}：氏名（必須）`;
        fields[1].setAttribute('id', `${idBase}name`);
        fields[1].setAttribute('name', `properties[参加者${idx}_氏名]`);
        fields[1].setAttribute('autocomplete', 'name');
        fields[2].setAttribute('for', `${idBase}age`);
        fields[2].textContent = `同行者${idx}：年齢（必須）`;
        fields[3].setAttribute('id', `${idBase}age`);
        fields[3].setAttribute('name', `properties[参加者${idx}_年齢]`);
        fields[3].setAttribute('min', '0');
        fields[4].setAttribute('for', `${idBase}gender`);
        fields[4].textContent = `同行者${idx}：性別`;
        fields[5].setAttribute('id', `${idBase}gender`);
        fields[5].setAttribute('name', `properties[参加者${idx}_性別]`);
        fields[6].setAttribute('for', `${idBase}tel`);
        fields[6].textContent = `同行者${idx}：電話（例: 090-1234-5678）`;
        fields[7].setAttribute('id', `${idBase}tel`);
        fields[7].setAttribute('name', `properties[参加者${idx}_電話]`);
        fields[7].setAttribute('autocomplete', 'tel');
        fields[7].setAttribute('placeholder', '090-1234-5678');
        const errors = node.querySelectorAll('[data-field-error]');
        if (errors[0]) {
          errors[0].id = `${idBase}name-error`;
          fields[1].setAttribute('aria-describedby', errors[0].id);
        }
        if (errors[1]) {
          errors[1].id = `${idBase}age-error`;
          fields[3].setAttribute('aria-describedby', errors[1].id);
        }
        if (errors[2]) {
          errors[2].id = `${idBase}gender-error`;
          fields[5].setAttribute('aria-describedby', errors[2].id);
        }
        if (errors[3]) {
          errors[3].id = `${idBase}tel-error`;
          fields[7].setAttribute('aria-describedby', errors[3].id);
        }
        return node;
      }

      function getAttendeeRows() {
        return Array.from(grid.querySelectorAll('.tour-attendee'));
      }

      function getAttendeeRowFields(row) {
        if (!row) return {};
        const inputs = row.querySelectorAll('input, select');
        return {
          nameInput: inputs[0],
          ageInput: inputs[1],
          genderInput: inputs[2],
          telInput: inputs[3],
        };
      }

      function applyAttendeeStateToRow(row, prev = {}) {
        const { nameInput, ageInput, genderInput, telInput } = getAttendeeRowFields(row);
        if (nameInput && prev.name) nameInput.value = prev.name;
        if (ageInput && prev.age) ageInput.value = prev.age;
        if (genderInput && prev.gender) genderInput.value = prev.gender;
        if (telInput && prev.tel) telInput.value = prev.tel;
      }

      function createAttendeeRow(index, attendeeState = {}) {
        const row = buildRowTemplate(index);
        applyAttendeeStateToRow(row, attendeeState);
        return row;
      }

      function createAttendeeRowsFragment(count, attendeeStates) {
        const fragment = document.createDocumentFragment();
        for (let i = 0; i < count; i += 1) {
          fragment.appendChild(createAttendeeRow(i, attendeeStates[i] || {}));
        }
        return fragment;
      }

      function replaceAttendeeRows(count, attendeeStates) {
        grid.innerHTML = '';
        grid.appendChild(createAttendeeRowsFragment(count, attendeeStates));
      }

      function updateAttendeeRenderState(count) {
        status.textContent = `同行者数を ${count} 名に更新しました。`;
        fieldset.hidden = count === 0;
        updateMinorConsentVisibility();
        syncPresaleVisibilityWithProductForm();
      }

      function applyAttendeeInlineError(inputEl, message, options = {}) {
        const { showErrors = false } = options;
        return showErrors ? showFieldError(inputEl, message) : false;
      }

      function createAttendeeValidationFailure({ message, inputEl, inlineMessage, showErrors = false } = {}) {
        return {
          ok: false,
          msg: String(message || ''),
          inlineErrorShown: applyAttendeeInlineError(inputEl, inlineMessage, { showErrors }),
          focusEl: inputEl || null,
        };
      }

      function getAttendeeFieldInput(row, fieldName) {
        const { nameInput, ageInput, genderInput, telInput } = getAttendeeRowFields(row);
        if (fieldName === 'name') return nameInput || null;
        if (fieldName === 'age') return ageInput || null;
        if (fieldName === 'gender') return genderInput || null;
        if (fieldName === 'tel') return telInput || null;
        return null;
      }

      function getAttendeeFieldValidationResult(fieldName, value, attendeeNumber) {
        const helpers = getMFTourFormHelpers();
        if (typeof helpers.getAttendeeFieldValidation === 'function') {
          return helpers.getAttendeeFieldValidation({
            fieldName,
            value,
            attendeeNumber,
          });
        }

        const normalizedValue = String(value ?? '').trim();
        if (fieldName === 'name') {
          if (normalizedValue) return { ok: true, fieldName: 'name', message: '', inlineMessage: '' };
          return {
            ok: false,
            fieldName: 'name',
            message: `同行者${attendeeNumber} の氏名を入力してください。`,
            inlineMessage: '氏名を入力してください。',
          };
        }

        if (fieldName === 'age') {
          if (!normalizedValue) {
            return {
              ok: false,
              fieldName: 'age',
              message: `同行者${attendeeNumber} の年齢を入力してください。`,
              inlineMessage: '年齢を入力してください。',
            };
          }
          const age = parseFiniteInt(normalizedValue);
          if (Number.isFinite(age) && age >= 0) {
            return { ok: true, fieldName: 'age', message: '', inlineMessage: '' };
          }
          return {
            ok: false,
            fieldName: 'age',
            message: `同行者${attendeeNumber} の年齢を正しく入力してください。`,
            inlineMessage: '年齢を正しく入力してください。',
          };
        }

        if (fieldName === 'tel') {
          if (!normalizedValue || isValidPhoneNumber(normalizedValue)) {
            return { ok: true, fieldName: 'tel', message: '', inlineMessage: '' };
          }
          return {
            ok: false,
            fieldName: 'tel',
            message: `同行者${attendeeNumber} の電話番号を正しく入力してください。`,
            inlineMessage: '電話番号を正しく入力してください（例: 090-1234-5678）。',
          };
        }

        return { ok: true, fieldName: String(fieldName || ''), message: '', inlineMessage: '' };
      }

      function validateAttendeeField(inputEl, row, index, options = {}) {
        const { showErrors = false } = options;
        if (!inputEl || !row) return { ok: true, fieldName: '', message: '', inlineMessage: '' };

        const { nameInput, ageInput, telInput } = getAttendeeRowFields(row);
        let fieldName = '';
        if (inputEl === nameInput) fieldName = 'name';
        if (inputEl === ageInput) fieldName = 'age';
        if (inputEl === telInput) fieldName = 'tel';

        if (!fieldName) {
          if (showErrors) clearFieldError(inputEl);
          return { ok: true, fieldName: '', message: '', inlineMessage: '' };
        }

        const result = getAttendeeFieldValidationResult(fieldName, inputEl.value, index + 1);
        if (result.ok) {
          if (showErrors) clearFieldError(inputEl);
          return result;
        }

        if (showErrors) showFieldError(inputEl, result.inlineMessage);
        return result;
      }

      function validateAttendeeRowFields(row, index, options = {}) {
        if (!row) return { ok: false, results: [] };
        const validations = ['name', 'age', 'tel'].map((fieldName) =>
          validateAttendeeField(getAttendeeFieldInput(row, fieldName), row, index, options)
        );
        return {
          ok: validations.every((result) => result && result.ok),
          results: validations,
        };
      }

      function validateAttendeeRow(row, index, options = {}) {
        const { showErrors = false } = options;
        if (!row) {
          return { ok: false, msg: '同行者フォームの生成に失敗しました。', inlineErrorShown: false };
        }

        const attendeeNumber = index + 1;
        const attendeeState = collectAttendeeRowState(row);
        const helpers = getMFTourFormHelpers();
        const fieldValidations =
          typeof helpers.getAttendeeRowFieldValidations === 'function'
            ? helpers.getAttendeeRowFieldValidations({
                attendee: attendeeState,
                attendeeNumber,
              })
            : {
                name: getAttendeeFieldValidationResult('name', attendeeState.name, attendeeNumber),
                age: getAttendeeFieldValidationResult('age', attendeeState.age, attendeeNumber),
                tel: getAttendeeFieldValidationResult('tel', attendeeState.tel, attendeeNumber),
              };
        const firstFailure = ['name', 'age', 'tel']
          .map((fieldName) => fieldValidations[fieldName])
          .find((result) => result && !result.ok);
        if (firstFailure) {
          return createAttendeeValidationFailure({
            message: firstFailure.message,
            inputEl: getAttendeeFieldInput(row, firstFailure.fieldName),
            inlineMessage: firstFailure.inlineMessage,
            showErrors,
          });
        }

        return { ok: true };
      }

      function applyAttendeeValidationFailure(result) {
        if (!result || result.ok || !result.focusEl) return result;
        result.focusEl.focus();
        return result;
      }

      function getConsentElements() {
        return {
          consentCb: form.querySelector('[data-tour-consent-input]'),
          minorWrap: form.querySelector('[data-tour-minor-consent-wrapper]'),
          minorInput: form.querySelector('[data-tour-minor-consent-input]'),
        };
      }

      function notifyConsentChange() {
        const { consentCb } = getConsentElements();
        if (consentCb) consentCb.dispatchEvent(new Event('change', { bubbles: true }));
      }

      function syncPresaleVisibilityWithProductForm() {
        const productForm = typeof form.closest === 'function' ? form.closest('product-form') : null;
        if (productForm && typeof productForm.syncPresaleVisibility === 'function') {
          productForm.syncPresaleVisibility();
        }
      }

      function setBirthdateError(message) {
        if (!bdError) return;
        bdError.textContent = message || '';
        bdError.hidden = !message;
      }

      function getApplicantAge() {
        const birthdateIso = getApplicantBirthdateIso();
        if (!birthdateIso || !MINOR_CONSENT_REFERENCE_DATE_ISO) return null;
        return calculateAgeOnReferenceDate(birthdateIso, MINOR_CONSENT_REFERENCE_DATE_ISO);
      }

      function getAttendeeAges() {
        if (IS_AGE_PRICING_TOUR || !fieldset) return [];
        return Array.from(fieldset.querySelectorAll('.tour-attendees__grid input[type="number"]'))
          .map((el) => parseFiniteInt(el.value))
          .filter((age) => Number.isFinite(age) && age >= 0);
      }

      function evaluateMinorConsentState() {
        const applicantBirthdateIso = getApplicantBirthdateIso();
        const attendeeAges = getAttendeeAges();
        const helpers = getMFTourFormHelpers();
        if (typeof helpers.getMinorConsentRequirement === 'function') {
          return helpers.getMinorConsentRequirement({
            applicantBirthdateIso,
            referenceDateIso: MINOR_CONSENT_REFERENCE_DATE_ISO,
            attendeeAges,
          });
        }

        const applicantAge = getApplicantAge();
        return {
          applicantAge,
          attendeeAges,
          requiresConsent: hasMinorParticipant(applicantAge, attendeeAges),
        };
      }

      function shouldShowMinorConsent() {
        return evaluateMinorConsentState().requiresConsent;
      }

      function applyMinorConsentState(show) {
        const { minorWrap, minorInput } = getConsentElements();
        if (!minorWrap) return;
        minorWrap.hidden = !show;
        if (!show && minorInput) minorInput.checked = false;
      }

      function applyMinorConsentVisibility(show, options = {}) {
        const { notify = false } = options;
        applyMinorConsentState(show);
        if (notify) notifyConsentChange();
      }

      function updateMinorConsentVisibility(options = {}) {
        applyMinorConsentVisibility(shouldShowMinorConsent(), options);
        syncPresaleVisibilityWithProductForm();
      }

      function refreshMinorConsentState() {
        updateMinorConsentVisibility({ notify: true });
      }

      function refreshParticipantState() {
        if (IS_AGE_PRICING_TOUR) {
          const syncAlreadyInProgress = agePricingSyncing;
          syncAgePricingState();
          if (syncAlreadyInProgress) refreshMinorConsentState();
          return;
        }
        refreshMinorConsentState();
      }

      function applyAgePricingStateSnapshot(snapshot) {
        if (!agePricingStateInput) return null;
        applyAgePricingContractSnapshot(snapshot);
        return snapshot;
      }

      function updateAgePricingStatePresentation(snapshot, message) {
        if (!snapshot) return;
        setBirthdateError(message);
        updateAgePricingPriceVisibility(snapshot.state);
      }

      function setAgePricingState(stateValue, message, matchedVariant, ageValue) {
        const snapshot = createAgePricingContractSnapshot(stateValue, matchedVariant, ageValue);
        const appliedSnapshot = applyAgePricingStateSnapshot(snapshot);
        updateAgePricingStatePresentation(appliedSnapshot, message);
        return appliedSnapshot;
      }

      function getAgePricingDisplayElements() {
        if (!sectionRoot || !IS_AGE_PRICING_TOUR) return null;
        const container = sectionRoot.querySelector('[data-age-pricing-price-container]');
        if (!container) return null;
        return {
          container,
          placeholder: container.querySelector('[data-age-pricing-message]'),
          priceContent: container.querySelector('[data-age-pricing-price-content]'),
          bandDisplay: container.querySelector('[data-age-pricing-band-display]'),
        };
      }

      function getAgePricingDisplayState(stateValue) {
        const { bandValue } = getAgePricingContractState();
        const inventoryEl =
          sectionRoot && IS_AGE_PRICING_TOUR && INVENTORY_ELEMENT_ID
            ? sectionRoot.querySelector(`#${escapeCss(INVENTORY_ELEMENT_ID)}`)
            : null;
        const hasInventoryText = !!String((inventoryEl && inventoryEl.innerText) || '').trim();
        const helpers = getMFTourFormHelpers();
        if (typeof helpers.getAgePricingDisplayState === 'function') {
          return helpers.getAgePricingDisplayState({
            state: stateValue,
            bandValue,
            hasInventoryText,
          });
        }
        const normalizedBandValue = String(bandValue || '').trim();
        const shouldShowPrice = stateValue === 'ok';
        return {
          normalizedBandValue,
          shouldShowPrice,
          shouldShowBand: shouldShowPrice && !!normalizedBandValue,
          shouldShowInventory: shouldShowPrice && hasInventoryText,
        };
      }

      function updateAgePricingPriceVisibility(stateValue) {
        const displayEls = getAgePricingDisplayElements();
        if (!displayEls) return;
        const { placeholder, priceContent, bandDisplay } = displayEls;
        const displayState = getAgePricingDisplayState(stateValue);

        if (placeholder) placeholder.classList.toggle('hidden', displayState.shouldShowPrice);
        if (priceContent) priceContent.classList.toggle('hidden', !displayState.shouldShowPrice);
        if (bandDisplay) {
          bandDisplay.textContent = displayState.normalizedBandValue;
          bandDisplay.classList.toggle('hidden', !displayState.shouldShowBand);
        }
        updateAgePricingInventoryVisibility(displayState.shouldShowInventory);
      }

      function updateAgePricingInventoryVisibility(shouldShowInventory) {
        if (!sectionRoot || !IS_AGE_PRICING_TOUR || !INVENTORY_ELEMENT_ID) return;
        const inventoryEl = sectionRoot.querySelector(`#${escapeCss(INVENTORY_ELEMENT_ID)}`);
        if (!inventoryEl) return;
        inventoryEl.dataset.agePricingInventory = shouldShowInventory ? 'ready' : 'locked';
        inventoryEl.classList.toggle('hidden', !shouldShowInventory);
      }

      function getVariantSelectsRoot() {
        return sectionRoot ? sectionRoot.querySelector('variant-selects') : null;
      }

      function getSelectedVariantOptionValues(variantSelects) {
        if (!variantSelects) return [];
        const selects = Array.from(variantSelects.querySelectorAll('select'));
        if (selects.length) return selects.map((select) => String(select.value || ''));
        const fieldsets = Array.from(variantSelects.querySelectorAll('fieldset'));
        return fieldsets.map((fieldsetEl) => {
          const checked = fieldsetEl.querySelector('input[type="radio"]:checked');
          return checked ? String(checked.value || '') : '';
        });
      }

      function applyVariantSelectOptions(selects, targetVariant) {
        const changedInputs = [];
        selects.forEach((select, index) => {
          const targetValue = targetVariant.options[index];
          if (typeof targetValue !== 'string' || select.value === targetValue) return;
          select.value = targetValue;
          const targetOption = Array.from(select.options).find((option) => option.value === targetValue);
          if (targetOption) {
            Array.from(select.options).forEach((option) => option.removeAttribute('selected'));
            targetOption.setAttribute('selected', 'selected');
          }
          changedInputs.push(select);
        });
        return changedInputs;
      }

      function applyVariantRadioOptions(fieldsets, targetVariant) {
        const changedInputs = [];
        fieldsets.forEach((fieldsetEl, index) => {
          const targetValue = targetVariant.options[index];
          const targetInput = Array.from(fieldsetEl.querySelectorAll('input[type="radio"]')).find((input) => input.value === targetValue);
          if (targetInput && !targetInput.checked) {
            targetInput.checked = true;
            changedInputs.push(targetInput);
          }
        });
        return changedInputs;
      }

      function selectVariantByOptions(targetVariant) {
        const variantSelects = getVariantSelectsRoot();
        if (!variantSelects || !targetVariant) return false;
        const selectedOptions = getSelectedVariantOptionValues(variantSelects);
        const optionsAlreadySelected =
          selectedOptions.length === targetVariant.options.length &&
          selectedOptions.every((value, index) => value === String(targetVariant.options[index] || ''));

        const selects = Array.from(variantSelects.querySelectorAll('select'));
        const changedInputs = selects.length
          ? applyVariantSelectOptions(selects, targetVariant)
          : applyVariantRadioOptions(Array.from(variantSelects.querySelectorAll('fieldset')), targetVariant);

        const helpers = getMFTourFormHelpers();
        const syncPlan =
          typeof helpers.getAgePricingVariantSyncPlan === 'function'
            ? helpers.getAgePricingVariantSyncPlan({
                pendingVariantId: pendingAgePricingVariantId,
                targetVariantId: targetVariant.id,
                optionsAlreadySelected,
                changedInputCount: changedInputs.length,
              })
            : (() => {
                const requestedVariantId = String(targetVariant.id || '');
                const alreadyPending =
                  pendingAgePricingVariantId === requestedVariantId && optionsAlreadySelected;
                return {
                  requestedVariantId,
                  shouldSync: !alreadyPending && !!requestedVariantId,
                  shouldDispatchChange: !alreadyPending && changedInputs.length > 0,
                };
              })();

        return applyPendingAgePricingVariantSync(syncPlan, changedInputs);
      }

      function applyPendingAgePricingVariantSync(syncPlan, changedInputs) {
        if (!syncPlan || !syncPlan.shouldSync) return true;
        pendingAgePricingVariantId = syncPlan.requestedVariantId;
        agePricingSyncing = true;
        if (!syncPlan.shouldDispatchChange) return true;
        const dispatchTarget = Array.isArray(changedInputs) ? changedInputs[changedInputs.length - 1] : null;
        if (dispatchTarget && typeof dispatchTarget.dispatchEvent === 'function') {
          dispatchTarget.dispatchEvent(new Event('change', { bubbles: true }));
        }
        return true;
      }

      function finalizeAgePricingVariantSync() {
        if (!IS_AGE_PRICING_TOUR) return;
        const helpers = getMFTourFormHelpers();
        const syncState =
          typeof helpers.resolveAgePricingVariantSync === 'function'
            ? helpers.resolveAgePricingVariantSync({
                pendingVariantId: pendingAgePricingVariantId,
                currentVariantId: getCurrentVariantId(),
              })
            : (() => {
                const currentVariantId = String(getCurrentVariantId() || '');
                const syncCompleted =
                  !!pendingAgePricingVariantId && currentVariantId === String(pendingAgePricingVariantId);
                return {
                  pendingVariantId: syncCompleted ? '' : String(pendingAgePricingVariantId || ''),
                  syncCompleted,
                  shouldReevaluate: !pendingAgePricingVariantId || syncCompleted,
                  syncing: !!pendingAgePricingVariantId && !syncCompleted,
                };
              })();

        applyResolvedAgePricingVariantSync(syncState);
      }

      function applyResolvedAgePricingVariantSync(syncState) {
        pendingAgePricingVariantId = syncState && syncState.pendingVariantId ? syncState.pendingVariantId : null;
        agePricingSyncing = !!(syncState && syncState.syncing);
        if (syncState && syncState.shouldReevaluate) {
          syncAgePricingState();
        }
      }

      function refreshConsentStateAfterAgePricing() {
        updateMinorConsentVisibility();
        notifyConsentChange();
      }

      function applyAgePricingStateAndRefresh(stateValue, message, matchedVariant, ageValue) {
        setAgePricingState(stateValue, message, matchedVariant, ageValue);
        refreshConsentStateAfterAgePricing();
      }

      function applyAgePricingApplication(application) {
        if (!application) return;
        applyAgePricingStateAndRefresh(
          application.state,
          application.message,
          application.matchedVariant,
          application.ageValue
        );
      }

      function createAgePricingEvaluation(state, message = '', matchedVariant = null, ageValue = null) {
        return { state, message, matchedVariant, ageValue };
      }

      function findAgePricingVariantFallback(ageValue) {
        const matches = normalizedAgeVariants().filter((variant) => {
          if (!Number.isFinite(variant.ageMin)) return false;
          if (ageValue < variant.ageMin) return false;
          if (Number.isFinite(variant.ageMax) && ageValue > variant.ageMax) return false;
          return true;
        });
        if (matches.length === 0) return { state: 'no_match', variant: null };
        if (matches.length > 1) return { state: 'duplicate', variant: null };
        return { state: 'matched', variant: matches[0] };
      }

      function evaluateMatchedAgePricingFallback(match, ageValue) {
        const matchedVariant = match.variant;
        if (String(getCurrentVariantId() || '') !== String(matchedVariant.id || '')) {
          return createAgePricingEvaluation('syncing', '価格区分を更新しています。', matchedVariant, ageValue);
        }
        if (!matchedVariant.available) {
          return createAgePricingEvaluation(
            'unavailable',
            '選択された価格区分は現在申し込めません。',
            matchedVariant,
            ageValue
          );
        }
        return createAgePricingEvaluation('ok', '', matchedVariant, ageValue);
      }

      function evaluateAgePricingWithoutHelper(birthdateIso) {
        if (!birthdateIso) return createAgePricingEvaluation('pending');
        if (!AGE_REFERENCE_DATE_ISO) {
          return createAgePricingEvaluation('missing_reference_date', 'ツアー日付が設定されていません。');
        }

        const ageValue = calculateAgeOnReferenceDate(birthdateIso, AGE_REFERENCE_DATE_ISO);
        if (!Number.isFinite(ageValue)) {
          return createAgePricingEvaluation('invalid_birthdate', '生年月日を正しく入力してください。');
        }

        const match = findAgePricingVariantFallback(ageValue);
        if (match.state === 'no_match') {
          return createAgePricingEvaluation('no_match', '該当する価格区分が見つかりません。', null, ageValue);
        }
        if (match.state === 'duplicate') {
          return createAgePricingEvaluation('duplicate', '価格区分の設定が重複しています。', null, ageValue);
        }
        return evaluateMatchedAgePricingFallback(match, ageValue);
      }

      function evaluateAgePricingInput() {
        const birthdateIso = getApplicantBirthdateIso();
        const helpers = getMFTourFormHelpers();
        if (typeof helpers.evaluateAgePricing === 'function') {
          return helpers.evaluateAgePricing({
            applicantBirthdateIso: birthdateIso,
            referenceDateIso: AGE_REFERENCE_DATE_ISO,
            agePricingVariants: AGE_PRICING_VARIANTS,
            currentVariantId: getCurrentVariantId(),
          });
        }

        // Fallback implementation when MFTourFormHelpers.evaluateAgePricing is unavailable.
        // Keep this in sync with the helper by using the same normalizedAgeVariants,
        // getCurrentVariantId, and createAgePricingEvaluation flow.
        return evaluateAgePricingWithoutHelper(birthdateIso);
      }

      function applyAgePricingEvaluation(evaluation) {
        const helpers = getMFTourFormHelpers();
        const plan =
          typeof helpers.getAgePricingEvaluationPlan === 'function'
            ? helpers.getAgePricingEvaluationPlan(evaluation)
            : (() => {
                const baseApplication = {
                  state: String(evaluation && evaluation.state ? evaluation.state : ''),
                  message: String((evaluation && evaluation.message) || ''),
                  matchedVariant: (evaluation && evaluation.matchedVariant) || null,
                  ageValue: Number.isFinite(evaluation && evaluation.ageValue) ? evaluation.ageValue : null,
                };
                const shouldSyncVariant =
                  baseApplication.state === 'syncing' && !!baseApplication.matchedVariant;
                return {
                  baseApplication,
                  shouldSyncVariant,
                  syncFailureApplication: shouldSyncVariant
                    ? {
                        state: 'variant_sync_failed',
                        message: '価格区分の更新に失敗しました。',
                        matchedVariant: baseApplication.matchedVariant,
                        ageValue: baseApplication.ageValue,
                      }
                    : null,
                };
              })();

        applyAgePricingApplication(plan.baseApplication);
        if (!plan.shouldSyncVariant || !plan.baseApplication.matchedVariant) return;
        if (!selectVariantByOptions(plan.baseApplication.matchedVariant)) {
          applyAgePricingApplication(plan.syncFailureApplication);
        }
      }

      function syncAgePricingState() {
        if (!IS_AGE_PRICING_TOUR || agePricingSyncing) return;
        const evaluation = evaluateAgePricingInput();
        applyAgePricingEvaluation(evaluation);
      }

      let lastRenderedCount = null;
      function captureAttendeeRenderState() {
        snapshot();
        return state;
      }

      function validateVisibleAttendeeRows(options = {}) {
        if (IS_AGE_PRICING_TOUR) return;
        getAttendeeRows().forEach((row, index) => {
          validateAttendeeRowFields(row, index, options);
        });
      }

      function applyAttendeeRender(count, attendeeStates) {
        lastRenderedCount = count;
        replaceAttendeeRows(count, attendeeStates);
        updateAttendeeRenderState(count);
        validateVisibleAttendeeRows({ showErrors: count > 0 });
      }

      function render(count) {
        applyAttendeeRender(count, captureAttendeeRenderState());
      }

      function getQuantity() {
        if (!qty) return 1;
        const val = parseFiniteInt(qty.value);
        return clampQty(Number.isFinite(val) ? val : 1);
      }

      function syncQuantityAndAttendees() {
        const effectiveMax = applyQtyMaxConstraint();
        updateLimitNote();
        const numericRaw = qty ? parseFiniteInt(qty.value) : null;
        const helpers = getMFTourFormHelpers();
        const plan =
          typeof helpers.getQuantitySyncPlan === 'function'
            ? helpers.getQuantitySyncPlan({
                rawQuantity: numericRaw,
                effectiveMax,
                lastRenderedCount,
                limitWarningActive,
                hasQuantityInput: !!qty,
                isAgePricingTour: IS_AGE_PRICING_TOUR,
              })
            : (() => {
                const safeRawQuantity = Number.isFinite(numericRaw) ? numericRaw : 1;
                const clampedQuantity = clampQty(qty ? safeRawQuantity : 1);
                const nextCount = attendeeCountFromQty(clampedQuantity);
                let shouldShowLimitWarning = false;
                let shouldHideLimitWarning = false;
                if (qty && Number.isFinite(effectiveMax)) {
                  if (safeRawQuantity > clampedQuantity) {
                    shouldShowLimitWarning = true;
                  } else if (limitWarningActive && clampedQuantity < effectiveMax) {
                    shouldHideLimitWarning = true;
                  }
                } else if (limitWarningActive) {
                  shouldHideLimitWarning = true;
                }
                return {
                  safeRawQuantity,
                  clampedQuantity,
                  nextCount,
                  shouldRender: nextCount !== lastRenderedCount,
                  shouldWriteQuantity: !!qty && safeRawQuantity !== clampedQuantity,
                  shouldShowLimitWarning,
                  shouldHideLimitWarning,
                };
              })();

        if (plan.shouldShowLimitWarning) {
          showLimitWarning();
        } else if (plan.shouldHideLimitWarning) {
          hideLimitWarning();
        }
        if (qty && plan.shouldWriteQuantity) {
          qty.value = plan.clampedQuantity;
          qty.setAttribute('value', plan.clampedQuantity);
        }
        if (plan.shouldRender) render(plan.nextCount);
      }

      syncQuantityAndAttendees();
      refreshParticipantState();
      syncPresaleVisibilityWithProductForm();

      if (qty) {
        ['input', 'change'].forEach((evt) => qty.addEventListener(evt, syncQuantityAndAttendees, { capture: true }));
      }

      form.addEventListener(
        'change',
        () => {
          setTimeout(() => {
            syncQuantityAndAttendees();
          }, 0);
        },
        true
      );

      form.addEventListener('tour:visibility-sync-request', () => {
        syncQuantityAndAttendees();
        refreshParticipantState();
        syncPresaleVisibilityWithProductForm();
      });

      if (IS_AGE_PRICING_TOUR && typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        subscribe(PUB_SUB_EVENTS.variantChange, ({ data } = {}) => {
          if (data && data.sectionId && String(data.sectionId) !== SECTION_ID) return;
          finalizeAgePricingVariantSync();
        });
      }

      if (IS_AGE_PRICING_TOUR && variantIdInput) {
        const variantIdObserver = new MutationObserver(() => {
          finalizeAgePricingVariantSync();
        });
        variantIdObserver.observe(variantIdInput, { attributes: true, attributeFilter: ['value'] });
        variantIdInput.addEventListener('change', finalizeAgePricingVariantSync);
      }

      if (qtyWrap) {
        qtyWrap.addEventListener(
          'click',
          (event) => {
            const plus = event.target.closest('button[name="plus"]');
            if (plus) {
              const effectiveMax = getEffectiveMax();
              const current = parseFiniteInt(qty.value);
              const numericCurrent = Number.isFinite(current) ? current : 1;
              if (Number.isFinite(effectiveMax) && numericCurrent >= effectiveMax) {
                event.preventDefault();
                event.stopImmediatePropagation();
                showLimitWarning();
                return;
              }
            }
            setTimeout(syncQuantityAndAttendees, 0);
          },
          true
        );
      }

      if (qty) {
        const mo = new MutationObserver(() => {
          const observedMax = parseFiniteInt(qty.getAttribute('max'));
          if (observedMax !== appliedMax) platformMax = observedMax;
          syncQuantityAndAttendees();
        });
        mo.observe(qty, { attributes: true, attributeFilter: ['value', 'max'] });
      }

      if (!IS_AGE_PRICING_TOUR) {
        fieldset.addEventListener(
          'input',
          (event) => {
            if (event.target && event.target.matches('input[type="number"], input, select')) {
              const attendeeRow = typeof event.target.closest === 'function' ? event.target.closest('.tour-attendee') : null;
              if (attendeeRow) {
                const attendeeRows = getAttendeeRows();
                const rowIndex = attendeeRows.indexOf(attendeeRow);
                validateAttendeeField(event.target, attendeeRow, rowIndex >= 0 ? rowIndex : 0, { showErrors: true });
              } else {
                clearFieldError(event.target);
              }
              refreshParticipantState();
            }
          },
          true
        );

        fieldset.addEventListener(
          'change',
          (event) => {
            if (event.target && event.target.matches('input[type="number"], input, select')) {
              const attendeeRow = typeof event.target.closest === 'function' ? event.target.closest('.tour-attendee') : null;
              if (attendeeRow) {
                const attendeeRows = getAttendeeRows();
                const rowIndex = attendeeRows.indexOf(attendeeRow);
                validateAttendeeField(event.target, attendeeRow, rowIndex >= 0 ? rowIndex : 0, { showErrors: true });
              } else {
                clearFieldError(event.target);
              }
              refreshParticipantState();
            }
          },
          true
        );
      }

      function createValidationResult(section, ok, message = '', inlineErrorShown = false) {
        return { section, ok, message, inlineErrorShown };
      }

      function createValidationFailure(section, message, inlineErrorShown = false) {
        return createValidationResult(section, false, message, inlineErrorShown);
      }

      function createBirthdateSectionValidationResult(hasBirthdate) {
        const helpers = getMFTourFormHelpers();
        if (typeof helpers.createBirthdateValidationResult === 'function') {
          return helpers.createBirthdateValidationResult({ hasBirthdate });
        }
        return hasBirthdate
          ? createValidationResult('birthdate', true)
          : createValidationFailure('birthdate', '申込者（代表者）の生年月日を入力してください。');
      }

      function createAgePricingSectionValidationResult(agePricingState, message) {
        const helpers = getMFTourFormHelpers();
        if (typeof helpers.createAgePricingValidationResult === 'function') {
          return helpers.createAgePricingValidationResult({
            isAgePricingTour: IS_AGE_PRICING_TOUR,
            agePricingState,
            message,
          });
        }
        if (!IS_AGE_PRICING_TOUR || agePricingState === 'ok') {
          return createValidationResult('agePricing', true);
        }
        return createValidationFailure('agePricing', message || '価格区分を確定できません。');
      }

      function createAttendeeSectionValidationResult(rowResults) {
        const helpers = getMFTourFormHelpers();
        if (typeof helpers.createAttendeeSectionValidationResult === 'function') {
          return helpers.createAttendeeSectionValidationResult({
            isAgePricingTour: IS_AGE_PRICING_TOUR,
            rowResults,
          });
        }
        if (IS_AGE_PRICING_TOUR) return createValidationResult('attendees', true);
        const firstFailure = Array.isArray(rowResults) ? rowResults.find((result) => result && !result.ok) : null;
        if (!firstFailure) return createValidationResult('attendees', true);
        return createValidationFailure(
          'attendees',
          firstFailure.msg || firstFailure.message || '',
          !!firstFailure.inlineErrorShown
        );
      }

      function getTourValidationContext() {
        const agePricingContractState = getAgePricingContractState();
        return {
          minorConsentRequired: shouldShowMinorConsent(),
          agePricingState: agePricingContractState.state,
          expectedVariantId: agePricingContractState.expectedVariantId,
        };
      }

      function createTourValidationStateFromResults(results) {
        const context = getTourValidationContext();
        const helpers = getMFTourFormHelpers();
        if (typeof helpers.createTourValidationState === 'function') {
          return helpers.createTourValidationState({
            ...context,
            results,
          });
        }
        if (typeof helpers.createTourValidationStateFallback === 'function') {
          return helpers.createTourValidationStateFallback({
            ...context,
            results,
          });
        }
        return buildTourValidationStateFallback({ ...context, results });
      }

      function getBirthdateValidationElements() {
        const bdSec = form.querySelector(`#${escapeCss(birthdateRootId)}`);
        if (!bdSec) return null;
        return {
          section: bdSec,
          yearSel: bdSec.querySelector('[data-birthdate-year]'),
          monthSel: bdSec.querySelector('[data-birthdate-month]'),
          daySel: bdSec.querySelector('[data-birthdate-day]'),
          hiddenInput: bdSec.querySelector('[data-birthdate-hidden]'),
        };
      }

      function setBirthdateFieldInvalidState(selectEl) {
        if (!selectEl || selectEl.value) return;
        selectEl.setAttribute('aria-invalid', 'true');
      }

      function focusBirthdateValidationField(birthdateEls) {
        if (birthdateEls && birthdateEls.yearSel) birthdateEls.yearSel.focus();
      }

      function applyBirthdateValidationFailure(result, birthdateEls) {
        if (!result || result.ok || !birthdateEls) return result;
        setBirthdateError(result.message);
        setBirthdateFieldInvalidState(birthdateEls.yearSel);
        setBirthdateFieldInvalidState(birthdateEls.monthSel);
        setBirthdateFieldInvalidState(birthdateEls.daySel);
        focusBirthdateValidationField(birthdateEls);
        return result;
      }

      function applyAgePricingValidationFailure(result, birthdateEls) {
        if (!result || result.ok) return result;
        focusBirthdateValidationField(birthdateEls);
        return result;
      }

      function validateBirthdateSection(options = {}) {
        const { showErrors = false } = options;
        const birthdateEls = getBirthdateValidationElements();
        if (!birthdateEls) return createValidationResult('birthdate', true);
        const result = createBirthdateSectionValidationResult(!!(birthdateEls.hiddenInput && birthdateEls.hiddenInput.value));
        if (result.ok) return result;

        return showErrors ? applyBirthdateValidationFailure(result, birthdateEls) : result;
      }

      function validateAgePricingSelection(options = {}) {
        const { showErrors = false } = options;
        if (!IS_AGE_PRICING_TOUR) return createAgePricingSectionValidationResult('ok', '');

        if (showErrors) syncAgePricingState();
        const { state: agePricingState } = getAgePricingContractState();
        const message = bdError && !bdError.hidden ? bdError.textContent : '価格区分を確定できません。';
        const result = createAgePricingSectionValidationResult(agePricingState, message);
        if (result.ok) return result;
        if (!showErrors) return result;
        return applyAgePricingValidationFailure(result, getBirthdateValidationElements());
      }

      function validateAttendeeSection(options = {}) {
        const { showErrors = false } = options;
        if (IS_AGE_PRICING_TOUR) return createAttendeeSectionValidationResult([]);

        const count = attendeeCountFromQty(getQuantity());
        if (showErrors) clearAllFieldErrors();
        const rowResults = [];
        for (let i = 0; i < count; i += 1) {
          const result = validateAttendeeRow(grid.children[i], i, { showErrors });
          rowResults.push(result);
          if (result.ok) continue;
          const handledResult = showErrors ? applyAttendeeValidationFailure(result) : result;
          rowResults[rowResults.length - 1] = handledResult;
          break;
        }
        return createAttendeeSectionValidationResult(rowResults);
      }

      function runTourValidationSections(options = {}) {
        return [
          validateBirthdateSection(options),
          validateAgePricingSelection(options),
          validateAttendeeSection(options),
        ];
      }

      function collectTourValidationState(options = {}) {
        const results = runTourValidationSections(options);
        return createTourValidationStateFromResults(results);
      }

      form.addEventListener('tour:validation-request', (event) => {
        const detail = event.detail || {};
        const showErrors = detail.mode === 'submit';
        detail.state = collectTourValidationState({ showErrors });
      });

      registry.instances[instanceKey] = { status: 'mounted', root: sectionRoot };
    } catch (error) {
      console.error('tour-attendees init error', error);
    }
  }

  function initAll(root) {
    getConfigNodes(root).forEach((configEl) => {
      mountTourAttendees(configEl);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll(document));
  } else {
    initAll(document);
  }

  document.addEventListener('shopify:section:load', (event) => {
    initAll(event.target);
  });

  document.addEventListener('product-info:loaded', (event) => {
    initAll(event.target);
  });
  })();
}
